import React from 'react';
import { 
  ArrowLeft, 
  Database, 
  Terminal, 
  Server, 
  Share2, 
  Check,
  ArrowRight
} from 'lucide-react';

const OklahomaModernizationCaseStudy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-green-500 text-white">
        <div className="container mx-auto px-6 py-16">
          <a href="/projects" className="inline-flex items-center text-blue-100 hover:text-white mb-8">
            <ArrowLeft className="mr-2" size={16} /> Back to Projects
          </a>
          <h1 className="text-4xl font-bold mb-4">Google Partner Project for Oklahoma Modernization</h1>
          <div className="flex flex-col md:flex-row md:items-center text-blue-100 mb-6">
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Database size={18} className="mr-2" /> 
              <span>Data Warehousing</span>
            </div>
            <div className="flex items-center mr-8 mb-2 md:mb-0">
              <Terminal size={18} className="mr-2" /> 
              <span>Infrastructure as Code</span>
            </div>
            <div className="flex items-center">
              <Server size={18} className="mr-2" /> 
              <span>Cloud Architecture</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section */}
      <div className="container mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/3 bg-blue-50 p-8">
              <h2 className="text-2xl font-bold mb-6 text-blue-900">Project Overview</h2>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Timeline</h3>
                <p className="text-gray-700">March 2023 - April 2024</p>
              </div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Role</h3>
                <p className="text-gray-700">Lead Platforms Engineer - Syntasa</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-blue-800">Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">GCP</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Terraform</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">BigQuery</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Dataproc</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">GKE</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Airflow</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">IAM</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">Key Manager</span>
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">CloudBuild</span>
                </div>
              </div>
            </div>
            <div className="md:w-2/3 p-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">The Challenge</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                As part of a Google Partner project for Oklahoma's state government modernization 
                initiative, our team was tasked with designing and implementing a modern data 
                warehouse infrastructure on Google Cloud Platform. The key challenges included:
              </p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Designing a scalable GCP data warehouse infrastructure that could handle diverse datasets</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Creating an extensible hub-and-spoke architecture to support multiple departments</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Building automated deployment processes for infrastructure provisioning</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Implementing robust security protocols for sensitive government data</span>
                </li>
                <li className="flex items-start">
                  <Check size={20} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">Coordinating with multiple stakeholders including Google engineers, state officials, and department IT teams</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The project required a solution that would not only modernize Oklahoma's data infrastructure 
                but also empower individual departments to create and manage their own data pipelines while 
                maintaining central governance and security standards.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Solution Section */}
      <div className="container mx-auto px-6 py-12">
        <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">The Solution</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Database className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Data Warehouse Design</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Designed GCP infrastructure with BigQuery as the core data warehouse</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented data processing pipelines using Dataproc for Spark workloads</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Deployed GKE clusters for containerized applications and services</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Share2 className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Hub-and-Spoke Architecture</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created a central hub for shared resources and governance</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Developed departmental spokes with dedicated resources and autonomy</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Established secure data sharing protocols between hub and spokes</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Terminal className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Terraform Infrastructure</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Developed modular Terraform code for infrastructure components</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created automated setup for Airflow spokes with one-click deployment</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Implemented infrastructure versioning and state management</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-full mr-3">
                <Server className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Airflow Orchestration</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Deployed Airflow on GKE for workflow orchestration</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Developed template DAGs for common data processing patterns</span>
              </li>
              <li className="flex items-start">
                <Check size={18} className="mr-2 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-gray-700">Created custom operators for Oklahoma-specific data sources</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Architecture Diagram Placeholder */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-12">
          <h3 className="text-xl font-bold mb-4 text-gray-800">Hub-and-Spoke Architecture</h3>
          <div className="bg-gray-100 p-4 rounded-lg h-64 flex items-center justify-center">
            <p className="text-gray-500">Hub-and-spoke architecture diagram would be displayed here</p>
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="bg-gray-100 py-12">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-800">Results & Impact</h2>
          
          <div className="bg-white p-8 rounded-xl shadow-md mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Project Outcomes</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Scalable Infrastructure</h4>
                <p className="text-gray-700">
                  Successfully deployed a flexible GCP infrastructure capable of handling Oklahoma's growing 
                  data needs with the ability to scale from gigabytes to petabytes of data.
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Departmental Autonomy</h4>
                <p className="text-gray-700">
                  Enabled 5 state departments to create and manage their own data pipelines within the 
                  first 3 months. Trained government IT experts to create and upgrade environments within a few days.
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Deployment Efficiency</h4>
                <p className="text-gray-700">
                  Reduced new department onboarding time from 4-6 weeks to just 2-3 days through 
                  automated Terraform infrastructure and template-based Airflow workflows.
                </p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-lg mb-2 text-gray-800">Cost Optimization</h4>
                <p className="text-gray-700">
                  Achieved 30% cost savings compared to initial estimates by implementing efficient 
                  resource sharing in the hub-and-spoke model and using pay-as-you-go services.
                </p>
              </div>
            </div>
          </div>
          
          {/* Key Learnings */}
          <div className="bg-white p-8 rounded-xl shadow-md mb-12">
            <h3 className="text-2xl font-bold mb-6 text-gray-800">Key Learnings</h3>
            <div className="space-y-4">
              <p className="text-gray-700">
                <span className="font-semibold">Multi-stakeholder coordination:</span> The project's success 
                hinged on effective communication between technical teams, Google engineers, and 
                government officials. Regular check-ins and clear documentation proved essential.
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Infrastructure as Code discipline:</span> Maintaining strict 
                version control and testing practices for Terraform code was critical for reliable 
                deployments across multiple departments.
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Balance between standardization and flexibility:</span> Finding 
                the right balance between standardized infrastructure and department-specific customizations 
                was an ongoing challenge that required constant refinement.
              </p>
              <p className="text-gray-700">
                <span className="font-semibold">Knowledge transfer strategy:</span> Creating comprehensive 
                documentation and training programs for state IT staff was as important as the technical 
                implementation for long-term sustainability.
              </p>
            </div>
          </div>
          
          {/* Next Case Study Navigation */}
          <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-md">
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Next Case Study
              </h3>
              <p className="text-gray-600">
                Data Visualization Improvements
              </p>
            </div>
            <a
              href="/projects/data-visualization"
              className="mt-4 md:mt-0 bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow hover:bg-blue-700 transition duration-300 flex items-center"
            >
              View Next Project <ArrowRight className="ml-2" size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OklahomaModernizationCaseStudy;